import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Download } from "lucide-react";
import { api, API } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";
import { DidYouKnow } from "../components/DidYouKnow";
import { ShareCard } from "../components/ShareCard";

const PdfLink = ({ slug }) => (
  <a
    href={`${API}/womens-pdf/${slug}`}
    target="_blank"
    rel="noreferrer"
    data-testid="woman-pdf-link"
    className="inline-flex items-center gap-2 border border-white/25 px-5 py-3 text-[0.68rem] font-bold uppercase tracking-[0.16em] text-white/80 transition-colors hover:border-maasai hover:text-ochre"
  >
    <Download size={14} /> One-page PDF
  </a>
);

const Stat = ({ label, value }) => (
  <div className="bg-black p-5">
    <p className="font-display text-2xl font-bold leading-none text-ochre md:text-3xl">{value}</p>
    <p className="mono mt-3 text-[0.55rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-400">
      {label}
    </p>
  </div>
);

export default function WomanDetail() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setD(null);
    setMissing(false);
    api
      .get(`/womens-game/${slug}`)
      .then((r) => setD(r.data))
      .catch(() => setMissing(true));
  }, [slug]);

  const subject = d?.player || d?.person;
  useSeo({
    title: subject ? `${subject.name} — The Women's Game — KickStartKenyaFootball` : "The Women's Game",
    description: subject ? subject.title || subject.role : "Kenyan women in football.",
  });

  if (missing)
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-black px-5" data-testid="woman-missing">
        <div className="text-center">
          <h1 className="display-3">We could not find that profile</h1>
          <Link to="/womens-game" className="btn-primary mt-8 inline-block">
            Back to the women&rsquo;s game
          </Link>
        </div>
      </div>
    );
  if (!d) return <div className="min-h-[60vh] bg-black" data-testid="woman-loading" />;

  const p = d.player;
  const person = d.person;

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="woman-detail-page">
      <div className="mx-auto max-w-[1100px] px-5 md:px-10">
        <Link
          to="/womens-game"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-zinc-500 hover:text-ochre"
          data-testid="woman-back"
        >
          <ArrowLeft size={14} /> The women&rsquo;s game
        </Link>

        {p ? (
          <>
            <p className="overline mt-10 text-ochre">
              Number {String(p.rank).padStart(2, "0")} · {p.track === "professional" ? "The professional game" : "The national game"}
            </p>
            <h1 className="display-1 mt-5">{p.name}</h1>
            <p className="mono mt-5 text-[0.62rem] uppercase leading-relaxed tracking-[0.16em] text-zinc-400">
              {p.position} · {p.club} · {p.country} · {p.era}
            </p>
            <p className="mt-6 max-w-3xl font-display text-lg uppercase leading-tight text-ochre md:text-2xl">
              {p.title}
            </p>

            <div className="mt-10 grid gap-px bg-zinc-800 sm:grid-cols-2 lg:grid-cols-5" data-testid="woman-scores">
              <Stat label="Total score of 80" value={p.total_score} />
              <Stat label="League strength (25)" value={p.scores.league_strength} />
              <Stat label="Prestige (20)" value={p.scores.prestige} />
              <Stat label="Titles won (20)" value={p.scores.titles} />
              <Stat label="Impact (15)" value={p.scores.impact} />
            </div>

            <div className="mt-14 space-y-7" data-testid="woman-bio">
              {p.sections.map(([heading, body]) => (
                <div key={heading}>
                  <h2 className="font-display text-base uppercase tracking-tight text-ochre md:text-lg">
                    {heading}
                  </h2>
                  <p className="mt-2 text-base leading-relaxed text-zinc-300 md:text-lg">{body}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 border-l-2 border-maasai bg-[#0b0b0b] p-6" data-testid="woman-honours">
              <p className="overline text-ochre">Honours and milestones</p>
              <ul className="mt-3 space-y-2">
                {p.honours.map((h) => (
                  <li key={h} className="text-sm leading-relaxed text-zinc-300 md:text-base">
                    {h}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-6 border-l-2 border-zinc-700 bg-[#0b0b0b] p-6" data-testid="woman-career">
              <p className="overline text-ochre">Career record</p>
              <p className="mt-3 text-sm leading-relaxed text-zinc-300 md:text-base">{p.career}</p>
              {p.caps ? (
                <p className="mono mt-4 text-[0.58rem] uppercase tracking-[0.16em] text-zinc-400">
                  Kenya: {p.caps}
                  {p.goals ? ` · ${p.goals} international goals` : ""}
                </p>
              ) : null}
            </div>

            <div className="mt-10">
              <DidYouKnow fact={p.fact} testId="woman-did-you-know" />
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <PdfLink slug={p.slug} />
              <ShareCard
                title={`${p.name} — The Women's Game`}
                text={`${p.name} — Number ${p.rank} in the KickStartKenyaFootball women's game archive. ${p.title}.`}
                label="Share this profile"
                testId="woman-share"
              />
            </div>

            <div
              className="mt-14 grid gap-px bg-zinc-800 sm:auto-cols-fr sm:grid-flow-col"
              data-testid="woman-neighbours"
            >
              {[d.neighbours.previous, d.neighbours.next].filter(Boolean).map((n) => (
                <Link
                  key={n.slug}
                  to={`/womens-game/${n.slug}`}
                  data-testid={`woman-neighbour-${n.slug}`}
                  className="group bg-black p-6 transition-colors hover:bg-[#0b0b0b]"
                >
                  <p className="mono text-[0.55rem] uppercase tracking-[0.18em] text-zinc-500">
                    Number {String(n.rank).padStart(2, "0")}
                  </p>
                  <p className="mt-2 font-display text-lg font-bold uppercase text-white group-hover:text-ochre">
                    {n.name}
                  </p>
                  <p className="mt-1 text-xs leading-snug text-zinc-400">{n.title}</p>
                </Link>
              ))}
            </div>
          </>
        ) : (
          <>
            <p className="overline mt-10 text-maasai">{person.category}</p>
            <h1 className="display-1 mt-5">{person.name}</h1>
            <p className="mono mt-5 text-[0.62rem] uppercase leading-relaxed tracking-[0.16em] text-ochre">
              {person.role}
            </p>
            <p className="mt-8 max-w-3xl text-base leading-relaxed text-zinc-300 md:text-lg" data-testid="person-body">
              {person.body}
            </p>
            <div className="mt-10 grid gap-px bg-zinc-800 sm:grid-cols-3" data-testid="person-numbers">
              {person.numbers.map((n) => (
                <Stat key={n.label} label={n.label} value={n.value} />
              ))}
            </div>
            <a
              href={person.source.url}
              target="_blank"
              rel="noreferrer"
              data-testid="person-source"
              className="mono mt-8 inline-flex items-center gap-2 text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500 transition-colors hover:text-ochre"
            >
              {person.source.label} <ArrowUpRight size={11} />
            </a>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <PdfLink slug={person.slug} />
              <ShareCard
                title={`${person.name} — Women In Football`}
                text={`${person.name} — ${person.role}, in the KickStartKenyaFootball women's game archive.`}
                label="Share this profile"
                testId="person-share"
              />
            </div>
          </>
        )}

        <Reveal>
          <Link
            to="/womens-game"
            className="mono mt-16 inline-flex items-center gap-2 text-[0.6rem] uppercase tracking-[0.18em] text-zinc-400 hover:text-ochre"
            data-testid="woman-back-bottom"
          >
            All of the women&rsquo;s game <ArrowUpRight size={12} />
          </Link>
        </Reveal>
      </div>
    </div>
  );
}
